import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, InputAdornment, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Tooltip,
  CircularProgress, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { listPayeesManage, createPayee, updatePayee, deletePayee, INDONESIAN_BANKS } from '../../services/fundRequestService';

const ALLOWED_ROLES = ['accounting', 'finance_manager', 'admin', 'director'];
const emptyForm = { name: '', bankName: '', accountNumber: '', accountHolder: '' };

export default function RekeningList() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const allowed = ALLOWED_ROLES.includes(role) || user?.role === 'Director';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI 0-based
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=add, else payee
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Guard terhadap respons basi: saat user mengetik cepat, respons request lama
  // bisa tiba setelah yang baru dan menimpa hasil. Hanya request terakhir dipakai.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!allowed) return;
    const myReq = ++reqIdRef.current;
    setLoading(true);
    try {
      const res = await listPayeesManage({ q, page: page + 1, pageSize });
      if (myReq !== reqIdRef.current) return; // respons basi -> abaikan
      setRows(res?.data || []);
      setTotal(res?.total || 0);
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      toast.error(e?.response?.data?.message || 'Gagal memuat daftar rekening');
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [allowed, q, page, pageSize]);

  useEffect(() => {
    const t = setTimeout(load, 300); // debounce search
    return () => clearTimeout(t);
  }, [load]);

  if (!allowed) return <Navigate to="/dashboard" replace />;

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      bankName: p.bankName || '',
      accountNumber: p.accountNumber || '',
      accountHolder: p.accountHolder || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nama rekening wajib diisi'); return; }
    setSaving(true);
    try {
      if (editing) {
        await updatePayee(editing.id, form);
        toast.success('Rekening diperbarui');
      } else {
        await createPayee(form);
        toast.success('Rekening ditambahkan');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Gagal menyimpan rekening');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deletePayee(deleteTarget.id);
      toast.success(res?.message || 'Rekening dihapus');
      setDeleteTarget(null);
      // kalau baris terakhir di halaman, mundur satu halaman
      if (rows.length === 1 && page > 0) setPage(page - 1); else load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Gagal menghapus rekening');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceIcon color="primary" /> Kelola Rekening
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Daftar rekening tujuan untuk pengajuan dana. Bisa ditambah, diubah, dan dihapus.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Tambah Rekening
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <TextField
          size="small" fullWidth placeholder="Cari nama, bank, no. rekening, atau pemilik…"
          value={q}
          onChange={(e) => { setPage(0); setQ(e.target.value); }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nama</TableCell>
              <TableCell>Bank</TableCell>
              <TableCell>No. Rekening</TableCell>
              <TableCell>Pemilik Rekening</TableCell>
              <TableCell align="right">Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={26} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>Belum ada rekening.</TableCell></TableRow>
            ) : rows.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                <TableCell>{p.bankName || '-'}</TableCell>
                <TableCell>{p.accountNumber || '-'}</TableCell>
                <TableCell>{p.accountHolder || '-'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Hapus"><IconButton size="small" color="error" onClick={() => setDeleteTarget(p)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Baris per halaman"
        />
      </TableContainer>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Rekening' : 'Tambah Rekening'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nama Rekening" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth autoFocus helperText="Mis. nama vendor / penerima" />
            <Autocomplete
              freeSolo
              options={INDONESIAN_BANKS}
              value={form.bankName}
              onChange={(e, val) => setForm({ ...form, bankName: val || '' })}
              onInputChange={(e, val) => setForm({ ...form, bankName: val })}
              renderInput={(params) => (
                <TextField {...params} label="Bank" placeholder="Pilih atau tulis nama bank" fullWidth />
              )}
            />
            <TextField label="No. Rekening" value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })} fullWidth />
            <TextField label="Nama Pemilik Rekening" value={form.accountHolder} onChange={(e) => setForm({ ...form, accountHolder: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Batal</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
            {editing ? 'Simpan' : 'Tambah'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Hapus Rekening?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Hapus rekening <b>{deleteTarget?.name}</b>{deleteTarget?.accountNumber ? ` (${deleteTarget.accountNumber})` : ''}? Pengajuan dana yang sudah dibuat tidak terpengaruh.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Batal</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting} startIcon={deleting ? <CircularProgress size={16} /> : null}>Hapus</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
